import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_FUEL_LOGS
} from '../config/appwrite';
import { ID, Query } from 'appwrite';

const MAX_ATTACHMENT_CHARS = 900000;

const toNumber = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/** Map Appwrite document → fuel row used by Fleet Maintenance. */
export const formatFuelLog = (doc) => ({
  id: doc.$id ?? doc.id,
  vehicleId: doc.vehicleId || '',
  plate: doc.plate || '',
  driver: doc.driver || '',
  project: doc.project || '',
  date: doc.fillDate || '',
  fillDate: doc.fillDate || '',
  previousOdometer: toNumber(doc.previousOdometer, 0),
  odometer: toNumber(doc.odometer, 0),
  liters: toNumber(doc.liters, 0),
  fuelCost: toNumber(doc.fuelCost, 0),
  kmTraveled: toNumber(doc.kmTraveled, null),
  consumptionKmPerL: toNumber(doc.consumptionKmPerL, null),
  paymentType: doc.paymentType || (doc.cardUsed ? 'card' : 'cash'),
  cardUsed: doc.cardUsed !== false && String(doc.paymentType || 'card') !== 'cash',
  fuelCardNumber: doc.fuelCardNumber || '',
  station: doc.station || '',
  attachmentPhoto: doc.attachmentPhoto || null,
  attachmentPhotoName: doc.attachmentPhotoName || '',
  submittedByEmail: doc.submittedByEmail || '',
  submittedByName: doc.submittedByName || ''
});

const toPayload = (fields) => {
  const previousOdometer = toNumber(fields.previousOdometer, 0) ?? 0;
  const odometer = toNumber(fields.odometer, 0) ?? 0;
  const liters = toNumber(fields.liters, 0) ?? 0;
  const fuelCost = toNumber(fields.fuelCost, 0) ?? 0;
  const kmTraveled = Math.max(0, odometer - previousOdometer);
  const consumptionKmPerL = liters > 0 ? kmTraveled / liters : null;
  const cardUsed = fields.cardUsed !== false && String(fields.paymentType || 'card') !== 'cash';
  const attachmentRaw = String(fields.attachmentPhoto || '');
  const attachmentPhoto =
    !cardUsed && attachmentRaw && attachmentRaw.length <= MAX_ATTACHMENT_CHARS
      ? attachmentRaw
      : '';

  return {
    vehicleId: String(fields.vehicleId || '').trim(),
    plate: String(fields.plate || '')
      .trim()
      .toUpperCase()
      .replace(/\s/g, ''),
    driver: String(fields.driver || '').trim(),
    project: String(fields.project || '').trim(),
    fillDate: String(fields.fillDate || fields.date || '').trim(),
    previousOdometer,
    odometer,
    liters,
    fuelCost,
    kmTraveled,
    consumptionKmPerL: consumptionKmPerL == null ? 0 : consumptionKmPerL,
    paymentType: cardUsed ? 'card' : 'cash',
    cardUsed,
    fuelCardNumber: cardUsed ? String(fields.fuelCardNumber || '').trim() : '',
    station: String(fields.station || '').trim(),
    attachmentPhoto,
    attachmentPhotoName: cardUsed ? '' : String(fields.attachmentPhotoName || '').trim(),
    submittedByEmail: String(fields.submittedByEmail || '').trim().toLowerCase(),
    submittedByName: String(fields.submittedByName || '').trim()
  };
};

export const getFuelLogErrorMessage = (error) => {
  if (!error) return 'Unknown error';
  const msg = error.message || error.response?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (error.code) return `Error ${error.code}`;
  return 'Request failed';
};

const runList = async (extraQueries) => {
  const all = [];
  let cursor = null;
  const limit = 100;

  for (let page = 0; page < 20; page += 1) {
    const queries = [Query.limit(limit), Query.orderDesc('fillDate'), ...extraQueries];
    if (cursor) queries.push(Query.cursorAfter(cursor));
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_FUEL_LOGS,
      queries
    );
    const docs = response.documents || [];
    all.push(...docs.map(formatFuelLog));
    if (docs.length < limit) break;
    cursor = docs[docs.length - 1].$id;
  }
  return all;
};

/** Fetch fuel logs for a vehicle id and/or plate. */
export const fetchFuelLogsForVehicle = async ({ vehicleId, plate } = {}) => {
  const all = [];
  const id = String(vehicleId || '').trim();
  const plateNorm = String(plate || '')
    .trim()
    .toUpperCase()
    .replace(/\s/g, '');

  if (id) {
    all.push(...(await runList([Query.equal('vehicleId', id)])));
  }
  if (plateNorm) {
    const byPlate = await runList([Query.equal('plate', plateNorm)]);
    const seen = new Set(all.map((r) => r.id));
    byPlate.forEach((row) => {
      if (!seen.has(row.id)) all.push(row);
    });
  }

  return all.sort((a, b) => String(b.date).localeCompare(String(a.date)));
};

/** Create a new fuel log record. */
export const createFuelLog = async (fields) => {
  const payload = toPayload(fields);
  if (!payload.vehicleId || !payload.fillDate) {
    throw new Error('vehicleId and fillDate are required.');
  }
  if (payload.liters <= 0) {
    throw new Error('liters must be greater than 0.');
  }
  if (payload.odometer <= 0) {
    throw new Error('odometer must be greater than 0.');
  }

  const created = await databases.createDocument(
    DATABASE_ID,
    COLLECTION_ID_FUEL_LOGS,
    ID.unique(),
    payload
  );
  return formatFuelLog(created);
};
